import React, { useState } from "react";
import { OverlayTrigger, Popover, Button } from "react-bootstrap";
import {
  updateAppointmentStatusByPatientId,
  getAppointmentById,
} from "../../api";
import "./UpdateStatus.css";
import CompleteAppointmentModal from "../FullCalendar/CompleteAppointment"; // Import the modal
import { useNavigate } from "react-router-dom"; // Import useNavigate

export function StatusPopover({ appointmentId, currentStatus, patientId, onUpdate }) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [loading, setLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false); // ✅ Controls modal visibility
  const [appointment, setAppointment] = useState(null);
  const navigate = useNavigate(); // Initialize useNavigate

  // Define status list with icons
  const statuses = [
    { label: "Pending", icon: "fa-circle-question", color: "text-gray" },
    {
      label: "New Patient",
      icon: "fa-triangle-exclamation",
      color: "text-danger",
    },
    { label: "Confirmed", icon: "fa-calendar-check", color: "text-success" },
    { label: "Arrived", icon: "fa-location-dot", color: "text-secondary" },
    { label: "In Session", icon: "fa-spinner", color: "text-info" },
    { label: "Completed", icon: "fa-circle-check", color: "text-primary" },
    { label: "No Show", icon: "fa-ban", color: "text-danger" },
    { label: "Cancelled", icon: "fa-circle-xmark", color: "text-danger" },
  ];

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // ✅ Handle status change logic
  const handleStatusChange = async (newStatus) => {
    if (newStatus === selectedStatus) return;
    setLoading(true);
    try {
      const response = await getAppointmentById(appointmentId);
      setAppointment(response);

      // ✅ If status is "Completed", trigger FollowUpSession logic
      if (newStatus === "Completed" && onUpdate) {
        const wasSaved = await onUpdate("Completed"); // This calls handleExternalCompletion from the ref
        if (!wasSaved) {
          navigate(`/patients/appointment/${patientId}/${appointmentId}`);
          console.log("❌ Cannot complete: No template selected.");
          setLoading(false);
          return;
        }

        // ✅ Show modal ONLY if session saved
        setShowModal(true);
        setShowPopover(false);
        setLoading(false);
        return;
      }

      // ✅ Regular status update
      await updateAppointmentStatusByPatientId(appointmentId, newStatus);
      setSelectedStatus(newStatus);
      onUpdate(newStatus, false); // Fallback
      setShowPopover(false);
    } catch (error) {
      console.error("❌ Failed to update status:", error);
    }
    setLoading(false);
  };

  const handleCompleteAppointment = async () => {
    try {
      // No status update here — we defer this to the invoice modal completion

      setShowModal(false); // ✅ Close modal only after user completes everything
    } catch (error) {
      console.error("❌ Failed during complete appointment logic:", error);
    }
  };

  // ✅ Popover component (No header & no lines)
 const statusPopover = (
  <Popover id={`status-popover-${appointmentId}`} className="custom-popover">
    <Popover.Body>
      {statuses.map(({ label, icon, color }) => {
        const isCurrent = selectedStatus === label;

        // 🔒 Disable rules:
        // - Completed: disable all others
        // - New Patient: allow only New Patient and Cancelled AND No Show
        // - Cancelled: allow only Cancelled
       const isDisabled =
  selectedStatus === "Completed" ||
  (selectedStatus === "New Patient" &&
    label !== "New Patient" &&
    label !== "Cancelled" &&
    label !== "No Show") ||
  (selectedStatus === "Cancelled" && label !== "Cancelled");

        return (
          <div
            key={label}
            className={`d-flex align-items-center p-2 status-item ${
              isCurrent ? "active" : ""
            } ${isDisabled && !isCurrent ? "text-muted disabled-status" : ""}`}
            onClick={() => {
              if (isDisabled && !isCurrent) return; // prevent update
              handleStatusChange(label);
            }}
            style={{
              cursor: isDisabled && !isCurrent ? "not-allowed" : "pointer",
              opacity: isDisabled && !isCurrent ? 0.5 : 1,
            }}
          >
            <i className={`fa-solid ${icon} ${color} me-2`}></i>
            <span>{label}</span>
          </div>
        );
      })}
    </Popover.Body>
  </Popover>
);



  return (
    <>
      {/* Status Dropdown */}
      <OverlayTrigger
        show={showPopover}
        trigger="click"
        placement="bottom"
        overlay={statusPopover}
        rootClose
        onToggle={(isOpen) => setShowPopover(isOpen)}
      >
        <Button
          variant=""
          className="btn-bigger"
          onClick={() => setShowPopover(!showPopover)}
        >
          {statuses.find(({ label }) => label === selectedStatus) ? (
            <i
              className={`fa-solid ${
                statuses.find(({ label }) => label === selectedStatus).icon
              } 
                          ${
                            statuses.find(
                              ({ label }) => label === selectedStatus
                            ).color
                          }`}
            ></i>
          ) : (
            <i className="fas fa-ellipsis-v"></i>
          )}
        </Button>
      </OverlayTrigger>

      {/* ✅ Modal Opens When "Completed" is Selected */}
      {showModal && (
        <CompleteAppointmentModal
          appointment={appointment}
          onHide={handleCloseModal}
          show={showModal}
          onComplete={handleCompleteAppointment} // Pass the function to handle completion
        />
      )}
    </>
  );
}
